import React from 'react';
import {Spin} from 'antd';
import {SettingOutlined, KeyOutlined} from '@ant-design/icons';
import {Switch, Route, useHistory, useLocation} from 'react-router-dom';
import {Header, Footer, UserMenu} from '@acuwize/shared';
import {AuthProvider, useAuth} from './context/auth-context';
import StandardsMenu from './components/standards-menu';
import Login from './pages/login';
import Marketing from './pages/marketing';
import Dashboard from './pages/dashboard';
import EditorPage from './pages/editor';
import AdminUsers from './pages/admin-users';
import AdminOidc from './pages/admin-oidc';

// AuthProvider wraps the shell so every screen and hook can read auth state
// from one place. AppShell does the auth+route branching:
//   - loading       → spinner
//   - anon + /      → <Marketing> standalone (its own nav/footer; portal
//                     chrome hidden so marketing controls the entire page)
//   - anon + /login → <Login> inside portal chrome (provider chooser)
//   - logged in     → portal chrome + <Switch> for app routes

function App() {
    return (
        <AuthProvider>
            <AppShell />
        </AuthProvider>
    );
}

function AppShell() {
    const {user, logout} = useAuth();
    const history = useHistory();
    const location = useLocation();
    const loggedIn = Boolean(user);
    const isAdmin = loggedIn && user.role === 'Administrator';

    if (user === undefined) {
        return (
            <div className="portal-app">
                <main className="portal-app__main">
                    <div className="portal-app__loading">
                        <Spin size="large" />
                    </div>
                </main>
            </div>
        );
    }

    // Marketing is the unauthenticated home page; it owns its own header
    // and footer so the portal chrome is suppressed when it renders.
    if (!loggedIn && location.pathname === '/') {
        return <Marketing />;
    }

    const adminItems = isAdmin
        ? [
            {
                key: 'admin-users',
                icon: <SettingOutlined />,
                label: 'Manage users',
                onClick: () => history.push('/admin/users'),
            },
            {
                key: 'admin-oidc',
                icon: <KeyOutlined />,
                label: 'OIDC providers',
                onClick: () => history.push('/admin/oidc'),
            },
        ]
        : null;

    // The editor route owns its own three-pane layout and needs full
    // viewport width — drop main's 1200px-centered column for that path.
    // Other routes (dashboard, admin, login) keep the centered chrome.
    const isEditorRoute = /^\/[^/]+\/standards\/[^/]+\/[^/]+/.test(location.pathname);
    const mainClass = isEditorRoute ? 'portal-app__main portal-app__main--fluid' : 'portal-app__main';

    return (
        <div className="portal-app">
            <Header
                productName="Tenet AI"
                workgroupSlot={loggedIn ? <StandardsMenu /> : null}
                userMenuSlot={loggedIn ? <UserMenu user={user} onLogout={logout} extraItems={adminItems} /> : null}
            />
            <main className={mainClass}>
                {loggedIn ? (
                    <Switch>
                        <Route path="/admin/users" exact><AdminUsers /></Route>
                        <Route path="/admin/oidc" exact><AdminOidc /></Route>
                        <Route path="/:env/standards/:wg/:key/:version?/:view?"><EditorPage /></Route>
                        <Route path="/" exact><Dashboard /></Route>
                        <Route><NotFound /></Route>
                    </Switch>
                ) : (
                    // Anon-with-non-marketing-path: always show Login (covers
                    // /login plus any other URL the user typed while signed out).
                    <Login />
                )}
            </main>
            <Footer tagline="Tenet AI portal — pre-release scaffolding" />
        </div>
    );
}

function NotFound() {
    return (
        <div className="portal-dashboard__placeholder">
            404 — page not found.
        </div>
    );
}

export default App;
